import React, { useState } from 'react';
import { UserProfile, Attendance } from '../types';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { collection, doc, deleteDoc, updateDoc, getDocs, query, where } from 'firebase/firestore';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertTriangle, UserCheck, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';

interface MergeUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  users: UserProfile[];
  attendance: Attendance[];
}

export default function MergeUsersModal({ isOpen, onClose, users, attendance }: MergeUsersModalProps) {
  const [sourceId, setSourceId] = useState('');
  const [destId, setDestId] = useState('');
  const [isMerging, setIsMerging] = useState(false);

  const allUsersSorted = [...users].sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''));
  const sourceUser = users.find(u => u.uid === sourceId);
  const destUser = users.find(u => u.uid === destId);

  const handleMerge = async () => {
    if (!sourceId || !destId) {
      toast.error('Please select both source and destination profiles.');
      return;
    }
    if (sourceId === destId) {
      toast.error('Source and destination profiles must be different.');
      return;
    }
    if (!sourceUser || !destUser) return;

    setIsMerging(true);
    try {
      // 1. Calculate merged certifications & points
      const mergedManualPoints = (destUser.manualPoints || 0) + (sourceUser.manualPoints || 0);
      const mergedOts = destUser.ots || sourceUser.ots;
      const mergedOcc = destUser.occ || sourceUser.occ;
      const mergedSdfpp = destUser.sdfpp || sourceUser.sdfpp;
      const mergedRoles = Array.from(new Set([...(destUser.roles || []), ...(sourceUser.roles || [])]));
      const mergedDepartments = Array.from(new Set([...(destUser.departments || []), ...(sourceUser.departments || [])]));

      // Calculate earliest Zavala Join Date
      let mergedZavalaJoinDate = destUser.zavalaJoinDate;
      if (sourceUser.zavalaJoinDate) {
        if (!mergedZavalaJoinDate || new Date(sourceUser.zavalaJoinDate) < new Date(mergedZavalaJoinDate)) {
          mergedZavalaJoinDate = sourceUser.zavalaJoinDate;
        }
      }

      // Calculate earliest SFI Join Date
      let mergedSfiJoinDate = destUser.sfiJoinDate;
      if (sourceUser.sfiJoinDate) {
        if (!mergedSfiJoinDate || new Date(sourceUser.sfiJoinDate) < new Date(mergedSfiJoinDate)) {
          mergedSfiJoinDate = sourceUser.sfiJoinDate;
        }
      }

      // Calculate latest SFI Expire Date
      let mergedSfiExpireDate = destUser.sfiExpireDate;
      if (sourceUser.sfiExpireDate) {
        if (!mergedSfiExpireDate || new Date(sourceUser.sfiExpireDate) > new Date(mergedSfiExpireDate)) {
          mergedSfiExpireDate = sourceUser.sfiExpireDate;
        }
      }

      // Calculate latest Zavala Expire Date
      let mergedZavalaExpireDate = destUser.zavalaExpireDate;
      if (sourceUser.zavalaExpireDate) {
        if (!mergedZavalaExpireDate || new Date(sourceUser.zavalaExpireDate) > new Date(mergedZavalaExpireDate)) {
          mergedZavalaExpireDate = sourceUser.zavalaExpireDate;
        }
      }

      // 2. Query and update Attendance documents
      const qSourceAttendance = query(collection(db, 'attendance'), where('userId', '==', sourceId));
      const qDestAttendance = query(collection(db, 'attendance'), where('userId', '==', destId));
      
      const [sourceAttSnap, destAttSnap] = await Promise.all([
        getDocs(qSourceAttendance),
        getDocs(qDestAttendance)
      ]);

      const destEventIds = new Set(destAttSnap.docs.map(doc => doc.data().eventId));

      for (const attDoc of sourceAttSnap.docs) {
        const data = attDoc.data();
        if (destEventIds.has(data.eventId)) {
          // Dest already has it, delete source's duplicate record
          await deleteDoc(doc(db, 'attendance', attDoc.id));
        } else {
          // Dest doesn't have it, transfer it to dest user
          await updateDoc(doc(db, 'attendance', attDoc.id), { userId: destId });
        }
      }

      // 3. Query and update Shifts documents
      const shiftsSnap = await getDocs(collection(db, 'shifts'));
      for (const shiftDoc of shiftsSnap.docs) {
        const data = shiftDoc.data();
        const assignedUserIds = data.assignedUserIds || [];
        if (assignedUserIds.includes(sourceId)) {
          let updatedIds = assignedUserIds.filter((id: string) => id !== sourceId);
          if (!updatedIds.includes(destId)) {
            updatedIds.push(destId);
          }
          await updateDoc(doc(db, 'shifts', shiftDoc.id), { assignedUserIds: updatedIds });
        }
      }

      // 4. Query and update Signups documents
      const qSignups = query(collection(db, 'signups'), where('volunteerUid', '==', sourceId));
      const signupsSnap = await getDocs(qSignups);
      for (const signupDoc of signupsSnap.docs) {
        await updateDoc(doc(db, 'signups', signupDoc.id), { 
          volunteerUid: destId,
          volunteerName: `${destUser.firstName} ${destUser.lastName}`,
          volunteerEmail: destUser.email
        });
      }

      // 5. Query and update HelpTickets documents
      const qTickets = query(collection(db, 'helpTickets'), where('reportedBy', '==', sourceId));
      const ticketsSnap = await getDocs(qTickets);
      for (const ticketDoc of ticketsSnap.docs) {
        await updateDoc(doc(db, 'helpTickets', ticketDoc.id), { reportedBy: destId });
      }

      // 6. Query and update Reports documents
      const qReports = query(collection(db, 'reports'), where('submittedByUid', '==', sourceId));
      const reportsSnap = await getDocs(qReports);
      for (const rDoc of reportsSnap.docs) {
        await updateDoc(doc(db, 'reports', rDoc.id), { submittedByUid: destId });
      }

      // 7. Query and update Profile Change Requests
      const qRequests = query(collection(db, 'profile_change_requests'), where('userId', '==', sourceId));
      const reqSnap = await getDocs(qRequests);
      for (const reqDoc of reqSnap.docs) {
        await updateDoc(doc(db, 'profile_change_requests', reqDoc.id), { userId: destId });
      }

      // 8. Query and update Messages
      const qMessages = query(collection(db, 'messages'), where('userId', '==', sourceId));
      const msgSnap = await getDocs(qMessages);
      for (const mDoc of msgSnap.docs) {
        await updateDoc(doc(db, 'messages', mDoc.id), {
          userId: destId,
          userName: `${destUser.firstName} ${destUser.lastName}`
        });
      }

      // 9. Query and update Timelogs (if emails differ)
      if (sourceUser.email && sourceUser.email.toLowerCase().trim() !== destUser.email.toLowerCase().trim()) {
        const qLogs = query(collection(db, 'timelogs'), where('email', '==', sourceUser.email.toLowerCase().trim()));
        const logsSnap = await getDocs(qLogs);
        for (const lDoc of logsSnap.docs) {
          await updateDoc(doc(db, 'timelogs', lDoc.id), { email: destUser.email.toLowerCase().trim() });
        }
      }

      // 10. Query and update ConRegistrations (if emails differ)
      if (sourceUser.email && sourceUser.email.toLowerCase().trim() !== destUser.email.toLowerCase().trim()) {
        const qConReg = query(collection(db, 'conRegistrations'), where('email', '==', sourceUser.email.toLowerCase().trim()));
        const conRegSnap = await getDocs(qConReg);
        for (const crDoc of conRegSnap.docs) {
          await updateDoc(doc(db, 'conRegistrations', crDoc.id), {
            email: destUser.email.toLowerCase().trim(),
            name: `${destUser.firstName} ${destUser.lastName}`
          });
        }
      }

      // 11. Update target user profile in Firestore
      await updateDoc(doc(db, 'users', destId), {
        manualPoints: mergedManualPoints,
        ots: mergedOts,
        occ: mergedOcc,
        sdfpp: mergedSdfpp,
        roles: mergedRoles,
        departments: mergedDepartments,
        department: destUser.department || sourceUser.department || '',
        sccNumber: destUser.sccNumber || sourceUser.sccNumber || '',
        cell: destUser.cell || sourceUser.cell || '',
        dob: destUser.dob || sourceUser.dob || '',
        shipName: destUser.shipName || sourceUser.shipName || '',
        zavalaJoinDate: mergedZavalaJoinDate || '',
        sfiJoinDate: mergedSfiJoinDate || '',
        sfiExpireDate: mergedSfiExpireDate || '',
        zavalaExpireDate: mergedZavalaExpireDate || '',
        status: (destUser.status === 'active' || sourceUser.status === 'active') ? 'active' : (destUser.status || 'active')
      });

      // 12. Delete source user document from Firestore
      await deleteDoc(doc(db, 'users', sourceId));

      toast.success(`Personnel records merged successfully. ${sourceUser.firstName} ${sourceUser.lastName} has been integrated into ${destUser.firstName} ${destUser.lastName}.`);
      onClose();
    } catch (error) {
      console.error(error);
      handleFirestoreError(error, OperationType.WRITE, 'users');
    } finally {
      setIsMerging(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card-bg border border-border-color text-[var(--foreground)] w-[100dvw] max-w-none md:max-w-2xl h-[100dvh] md:h-auto md:max-h-[90vh] overflow-y-auto shadow-2xl p-0 rounded-none md:rounded-lg">
        <DialogHeader className="px-6 py-6 border-b border-border-color bg-white/5">
          <DialogTitle className="uppercase text-accent-gold tracking-widest font-bold text-xl flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-accent-gold" /> Merge Crew Profiles
          </DialogTitle>
        </DialogHeader>

        <div className="p-6 space-y-6">
          <p className="text-xs text-text-secondary uppercase tracking-widest leading-relaxed">
            This utility merges a duplicate source crew member record into a target primary record. All points, attendance, certifications, and shifts are consolidated, and the duplicate account is deleted.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Source User Selector */}
            <div className="space-y-2">
              <label className="text-[0.65rem] uppercase font-bold text-lcars-red tracking-wider block">1. Source Profile (Deleted after merge)</label>
              <Select value={sourceId} onValueChange={setSourceId}>
                <SelectTrigger className="bg-[#0d1117] border-lcars-red focus:border-lcars-red/80 h-12 text-[var(--foreground)]">
                  <SelectValue placeholder="Select duplicate profile" />
                </SelectTrigger>
                <SelectContent className="bg-card-bg border-border-color text-[var(--foreground)]">
                  {allUsersSorted.map(u => (
                    <SelectItem key={u.uid} value={u.uid} className="focus:bg-lcars-red focus:text-[var(--foreground)]">
                      {u.firstName} {u.lastName} ({u.email}){u.status === 'inactive' ? ' [INACTIVE]' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Destination User Selector */}
            <div className="space-y-2">
              <label className="text-[0.65rem] uppercase font-bold text-lcars-blue tracking-wider block">2. Target Profile (Kept and updated)</label>
              <Select value={destId} onValueChange={setDestId}>
                <SelectTrigger className="bg-[#0d1117] border-lcars-blue focus:border-lcars-blue/80 h-12 text-[var(--foreground)]">
                  <SelectValue placeholder="Select primary profile" />
                </SelectTrigger>
                <SelectContent className="bg-card-bg border-border-color text-[var(--foreground)]">
                  {allUsersSorted.map(u => (
                    <SelectItem key={u.uid} value={u.uid} className="focus:bg-lcars-blue focus:text-[var(--foreground)]">
                      {u.firstName} {u.lastName} ({u.email}){u.status === 'inactive' ? ' [INACTIVE]' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* User Summaries Side by Side */}
          {(sourceUser || destUser) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white/5 p-4 rounded border border-border-color">
              {/* Source User Detail */}
              <div className="space-y-1">
                {sourceUser ? (
                  <>
                    <h4 className="font-bold text-lcars-red uppercase text-sm">Duplicate Profile</h4>
                    <p className="text-[var(--foreground)] text-xs">{sourceUser.firstName} {sourceUser.lastName}</p>
                    <p className="text-[var(--foreground)]/40 text-[0.65rem] font-mono">{sourceUser.email}</p>
                    <p className="text-[var(--foreground)]/60 text-[0.7rem] pt-1">
                      Ship: {sourceUser.shipName || 'Unspecified'} | Points: {sourceUser.manualPoints || 0}
                    </p>
                  </>
                ) : (
                  <p className="text-[var(--foreground)]/30 text-xs italic">No source selected</p>
                )}
              </div>

              {/* Destination User Detail */}
              <div className="space-y-1 border-t md:border-t-0 md:border-l border-border-color/30 pt-4 md:pt-0 md:pl-4">
                {destUser ? (
                  <>
                    <h4 className="font-bold text-lcars-blue uppercase text-sm">Primary Profile</h4>
                    <p className="text-[var(--foreground)] text-xs">{destUser.firstName} {destUser.lastName}</p>
                    <p className="text-[var(--foreground)]/40 text-[0.65rem] font-mono">{destUser.email}</p>
                    <p className="text-[var(--foreground)]/60 text-[0.7rem] pt-1">
                      Ship: {destUser.shipName || 'Unspecified'} | Points: {destUser.manualPoints || 0}
                    </p>
                  </>
                ) : (
                  <p className="text-[var(--foreground)]/30 text-xs italic">No target selected</p>
                )}
              </div>
            </div>
          )}

          {sourceUser && destUser && (
            <div className="bg-lcars-red/10 border border-lcars-red/30 p-4 rounded text-xs text-lcars-red font-bold uppercase tracking-wider space-y-1 leading-relaxed">
              <p>Warning: This action will permanently delete the source profile ({sourceUser.firstName} {sourceUser.lastName}).</p>
              <p>All certification records, shifts, and points will be safely merged into {destUser.firstName} {destUser.lastName}. This operation is irreversible.</p>
            </div>
          )}
        </div>

        <DialogFooter className="bg-transparent border-t border-border-color p-6 flex flex-col sm:flex-row gap-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isMerging}
            className="lcars-pill bg-lcars-tan text-black px-8 py-3 text-[0.8rem] font-bold uppercase tracking-widest"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleMerge}
            disabled={isMerging || !sourceId || !destId || sourceId === destId}
            className={`lcars-pill bg-lcars-red text-[var(--foreground)] px-12 py-3 text-[0.8rem] font-bold uppercase tracking-widest flex items-center justify-center gap-2 ${isMerging ? 'opacity-70' : ''}`}
          >
            {isMerging ? (
              <>
                <RefreshCcw className="w-4 h-4 animate-spin" /> MERGING RECORDS...
              </>
            ) : (
              <>
                <UserCheck className="w-4 h-4" /> Engage Merge
              </>
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
